
import React, { useEffect, useRef, useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import {
  Button,
  FieldTextInput,
  FieldSelect,
  FieldCurrencyInput,
  IconCollection,
  IconSpinner,
} from '../../components';
import { FormattedMessage } from 'react-intl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { connect } from 'react-redux';
import { saveOvertimeBillDraft, deleteOvertimeBillDraft } from '../EventProviderDashboardPage/EventProviderDashboardPage.duck';

import css from './BillReductionModal.module.css';

const { Money } = sdkTypes;

const parseOvertimeHoursToMinutes = v => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;

  // Accept: "H", "H.M" (decimal hours), or "H:MM"
  const timeMatch = s.match(/^([0-9]+)\s*:\s*([0-5]?[0-9])$/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const mins = parseInt(timeMatch[2], 10);
    if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null;
    return hours * 60 + mins;
  }

  const num = Number(s);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.round(num * 60);
};

const toComparableDate = dateStr => {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const normalized = dateStr.replace(/,(\d{4})$/, ', $1');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getLatestEventDate = eventDates => {
  if (!Array.isArray(eventDates) || eventDates.length === 0) return null;

  return eventDates
    .map(toComparableDate)
    .filter(Boolean)
    .reduce((latest, d) => (!latest || d.getTime() > latest.getTime() ? d : latest), null);
};

const uploadSingleFileToCloudinary = async file => {
  const cloudName =
    process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || process.env.REACT_APP_CLOUDINARY_NAME;
  const uploadPreset =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || process.env.REACT_APP_CLOUDINARY_PRESET;

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary env vars are missing (cloud name / upload preset).');
  }

  let resourceType = 'image';
  const fileType = (file?.type || '').toLowerCase();

  if (fileType.startsWith('image')) {
    resourceType = 'image';
  } else if (fileType.startsWith('video')) {
    resourceType = 'video';
  } else if (fileType.startsWith('application')) {
    resourceType = 'image';
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', 'overtimeBillDocuments');
  formData.append('resource_type', resourceType);
  formData.append('upload_preset', uploadPreset);

  const res = await window.fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    const message = err?.error?.message || 'File upload failed.';
    throw new Error(message);
  }

  const d = await res.json();
  return {
    secure_url: d.secure_url,
    public_id: d.public_id,
    resource_type: d.resource_type,
    original_filename: d.original_filename,
    bytes: d.bytes,
  };
};

const CreateInvoiceOvertimeProviderModalComponent = props => {
  const {
    intl,
    onSubmit,
    onClose,
    isLoading,
    transactionsData,
    config,
    submitting,
    currentUser,
    onSaveOvertimeBillDraft,
    onDeleteOvertimeBillDraft,
  } = props;
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [selectedTransactionProtectedData, setSelectedTransactionProtectedData] = useState({});
  const [saveDraftInProgress, setSaveDraftInProgress] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const lastHydratedDraftTxIdRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectedDocumentsRef = useRef([]);
  const lastLoggedTransactionIdRef = useRef(null);
  const lastAppliedOvertimeRateRef = useRef(null);
  const lastAppliedTotalRef = useRef(null);

  useEffect(() => {
    selectedDocumentsRef.current = Array.isArray(selectedDocuments) ? selectedDocuments : [];
  }, [selectedDocuments]);

  useEffect(() => {
    return () => {
      const arr = Array.isArray(selectedDocumentsRef.current) ? selectedDocumentsRef.current : [];
      arr?.forEach(d => {
        if (d && d.previewUrl) {
          try {
            URL.revokeObjectURL(d.previewUrl);
          } catch (e) {
            // no-op
          }
        }
      });
    };
  }, []);

  const txItems = Array.isArray(transactionsData?.transactions) ? transactionsData.transactions : [];

  const getDraftStorageKey = () => 'overtimeBillDrafts';

  const getExistingDraftForTx = transactionId => {
    const privateData = currentUser?.attributes?.profile?.privateData;
    const drafts = privateData?.[getDraftStorageKey()];
    return drafts?.[transactionId] || null;
  };

  const isSubmitDisabled = values => {
    if (submitting) return true;

    const selectedEvent = values?.eventName;
    const overtimeHours = values?.overtimeHours;
    const overtimeRate = values?.overtimeRate;
    const totalOvertimeCost = values?.totalOvertimeCost;

    // Overtime Request: Overtime Details are required, Additional Expenses are
    // optional, and the Event Update section (eventStatus / actualEndTime) is
    // always optional.
    const hasRequiredFields =
      !!selectedEvent &&
      !!overtimeHours &&
      !!overtimeRate &&
      !!totalOvertimeCost;

    const hasDoc =
      (Array.isArray(selectedDocuments) && selectedDocuments.length > 0) ||
      (Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0);
    const notUploading = !uploadInProgress;

    return !(hasRequiredFields && hasDoc && notUploading);
  };

  const hasAnyDraftInput = values => {
    const selectedEvent = values?.eventName;
    const eventStatus = values?.eventStatus;
    const actualEndTime = values?.actualEndTime;
    const overtimeHours = values?.overtimeHours;
    const overtimeRate = values?.overtimeRate;
    const totalOvertimeCost = values?.totalOvertimeCost;
    const overtimeReason = values?.overtimeReason;
    const expenseDraft = values?.expenseDraft || {};

    const hasText = v => typeof v === 'string' && v.trim().length > 0;
    const hasValue = v => v !== undefined && v !== null && String(v).trim() !== '';
    const hasMoneyValue =
      v =>
        !!v &&
        typeof v === 'object' &&
        Number.isFinite(v?.amount) &&
        typeof v?.currency === 'string' &&
        v.currency.length > 0;

    const hasExpenseDraftInput =
      hasText(expenseDraft?.category) ||
      hasValue(expenseDraft?.quantity) ||
      hasMoneyValue(expenseDraft?.cost);

    const hasSavedExpenses = Array.isArray(expenses) && expenses.length > 0;
    const hasDocs =
      (Array.isArray(selectedDocuments) && selectedDocuments.length > 0) ||
      (Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0);

    return (
      hasValue(selectedEvent) ||
      hasText(eventStatus) ||
      hasText(actualEndTime) ||
      hasValue(overtimeHours) ||
      hasMoneyValue(overtimeRate) ||
      hasMoneyValue(totalOvertimeCost) ||
      hasText(overtimeReason) ||
      hasExpenseDraftInput ||
      hasSavedExpenses ||
      hasDocs
    );
  };

  const buildDraftPayload = (values, transactionId) => {
    const selectedTx = transactionId ? txItems.find(i => i?.transactionId === transactionId) : null;

    const serializeMoneyMaybe = m => {
      if (!m) return null;
      const amount = m?.amount;
      const currency = m?.currency;
      const amountInt = parseInt(amount);
      const hasAmount = Number.isFinite(amountInt);
      const hasCurrency = typeof currency === 'string' && currency.length > 0;
      return hasAmount && hasCurrency ? { amount: amountInt, currency } : null;
    };

    return {
      v: 1,
      transactionId,
      listingId: selectedTx?.listing?.id,
      values: {
        eventName: values?.eventName || null,
        eventStatus: values?.eventStatus || '',
        actualEndTime: values?.actualEndTime || '',
        overtimeHours: values?.overtimeHours || '',
        overtimeRate: serializeMoneyMaybe(values?.overtimeRate),
        totalOvertimeCost: serializeMoneyMaybe(values?.totalOvertimeCost),
        overtimeReason: values?.overtimeReason || '',
      },
      expenses,
      documents: {
        uploaded: uploadedDocuments,
      },
    };
  };

  const uploadNewSelectedDocuments = async () => {
    const docs = Array.isArray(selectedDocuments) ? selectedDocuments : [];
    if (docs.length === 0) {
      return [];
    }

    setUploadInProgress(true);
    try {
      const uploads = await Promise.all(
        docs.map(d =>
          uploadSingleFileToCloudinary(d.file).catch(err => {
            const name = d?.fileName ? ` (${d.fileName})` : '';
            const message = err?.message || 'File upload failed.';
            throw new Error(`${message}${name}`);
          })
        )
      );

      const newUploaded = uploads.map((u, idx) => ({
        id: u.public_id || `${Date.now()}-${idx}`,
        url: u.secure_url,
        publicId: u.public_id,
        resourceType: u.resource_type,
        fileName: u.original_filename || docs?.[idx]?.fileName,
        bytes: u.bytes,
        uploadedAt: new Date().toISOString(),
      }));

      setUploadedDocuments(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        return [...arr, ...newUploaded];
      });

      setSelectedDocuments(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        arr.forEach(d => {
          if (d && d.previewUrl) {
            try {
              URL.revokeObjectURL(d.previewUrl);
            } catch (e) {
              // no-op
            }
          }
        });
        return [];
      });

      return newUploaded;
    } finally {
      setUploadInProgress(false);
    }
  };

  const hydrateFromDraft = (draft, form) => {
    if (!draft || !form || typeof form.change !== 'function') {
      return;
    }

    const draftValues = draft?.values || {};
    form.change('eventStatus', draftValues?.eventStatus || '');
    form.change('actualEndTime', draftValues?.actualEndTime || '');
    form.change('overtimeHours', draftValues?.overtimeHours || '');
    form.change('overtimeReason', draftValues?.overtimeReason || '');

    const currency = config?.currencyConfig?.currency || 'USD';
    if (draftValues?.overtimeRate?.amount && draftValues?.overtimeRate?.currency) {
      form.change('overtimeRate', new Money(draftValues.overtimeRate.amount, draftValues.overtimeRate.currency));
    } else {
      form.change('overtimeRate', null);
    }

    if (draftValues?.totalOvertimeCost?.amount && draftValues?.totalOvertimeCost?.currency) {
      form.change(
        'totalOvertimeCost',
        new Money(draftValues.totalOvertimeCost.amount, draftValues.totalOvertimeCost.currency)
      );
    } else {
      form.change('totalOvertimeCost', null);
    }

    const draftExpenses = Array.isArray(draft?.expenses) ? draft.expenses : [];
    setExpenses(draftExpenses);

    const draftUploadedDocs = Array.isArray(draft?.documents?.uploaded) ? draft.documents.uploaded : [];
    setUploadedDocuments(draftUploadedDocs);

    setDraftLoaded(true);
  };

  const handleAddExpense = (values, form) => {
    if (expenses.length >= 3) return;

    const draft = values?.expenseDraft || {};
    const category = (draft?.category || '').trim();
    const quantity = draft?.quantity;
    const cost = {
      amount: draft?.cost?.amount,
      currency: draft?.cost?.currency,
    };

    const hasQty = quantity !== undefined && quantity !== null && quantity !== '';
    const hasCost =
      Object.keys(cost).length > 0 && Object.hasOwn(cost, 'amount') && Object.hasOwn(cost, 'currency');

    if (!category || !hasQty || !hasCost) return;

    const nextExpense = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      category,
      quantity,
      cost,
    };

    setExpenses(prev => [...prev, nextExpense].slice(0, 3));
    if (form && typeof form.change === 'function') {
      form.change('expenseDraft.category', undefined);
      form.change('expenseDraft.quantity', undefined);
      form.change('expenseDraft.cost', undefined);
    }
  };

  const handleRemoveExpense = id => {
    setExpenses(expenses.filter(e => e.id !== id));
  };

  const persistDraftDocuments = (transactionId, nextUploadedDocs, values) => {
    if (!transactionId) return Promise.resolve();

    const draftPayload = buildDraftPayload(values, transactionId);
    const nextDraftPayload = {
      ...draftPayload,
      documents: {
        uploaded: nextUploadedDocs,
      },
    };

    return onSaveOvertimeBillDraft(transactionId, nextDraftPayload, getDraftStorageKey()).catch(() => { });
  };

  const handleRemoveDocument = docId => {
    setSelectedDocuments(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const removed = arr.find(d => d && d.id === docId);
      if (removed && removed.previewUrl) {
        try {
          URL.revokeObjectURL(removed.previewUrl);
        } catch (e) {
          // no-op
        }
      }
      return arr.filter(doc => doc.id !== docId);
    });
  };

  const handleRemoveUploadedDocument = (docId, values) => {
    const txId = values?.eventName;
    if (!docId || !txId) return;

    setUploadedDocuments(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const next = arr.filter(d => (d?.id || d?.publicId) !== docId);
      persistDraftDocuments(txId, next, values);
      return next;
    });
  };

  const handlePickDocument = () => {
    if (uploadInProgress) return;
    const selectedCount = Array.isArray(selectedDocuments) ? selectedDocuments.length : 0;
    const uploadedCount = Array.isArray(uploadedDocuments) ? uploadedDocuments.length : 0;
    if (selectedCount + uploadedCount >= 5) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleDocumentSelected = e => {
    try {
      setUploadError(null);

      const file = e?.target?.files?.[0];
      if (!file) return;

      const currentCount = Array.isArray(selectedDocuments) ? selectedDocuments.length : 0;
      const uploadedCount = Array.isArray(uploadedDocuments) ? uploadedDocuments.length : 0;
      if (currentCount + uploadedCount >= 5) return;

      const isImage = typeof file.type === 'string' && file.type.startsWith('image/');
      const previewUrl = isImage ? URL.createObjectURL(file) : null;

      const nextDoc = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file,
        fileName: file.name,
        bytes: file.size,
        type: file.type,
        previewUrl,
        selectedAt: new Date().toISOString(),
      };

      setSelectedDocuments(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        return [...arr, nextDoc].slice(0, 5);
      });
    } catch (err) {
      setUploadError(err?.message || 'File selection failed.');
    } finally {
      if (e?.target) {
        e.target.value = '';
      }
    }
  };

  return (
    <FinalForm
      onSubmit={async values => {
        setUploadError(null);

        const docs = Array.isArray(selectedDocuments) ? selectedDocuments : [];
        const alreadyUploadedDocs = Array.isArray(uploadedDocuments) ? uploadedDocuments : [];

        try {
          setUploadInProgress(true);

          const uploads =
            docs.length > 0
              ? await Promise.all(
                docs.map(d =>
                  uploadSingleFileToCloudinary(d.file).catch(err => {
                    const name = d?.fileName ? ` (${d.fileName})` : '';
                    const message = err?.message || 'File upload failed.';
                    throw new Error(`${message}${name}`);
                  })
                )
              )
              : [];

          const newlyUploadedDocsForState = uploads.map((u, idx) => ({
            id: u.public_id || `${Date.now()}-${idx}`,
            url: u.secure_url,
            publicId: u.public_id,
            resourceType: u.resource_type,
            fileName: u.original_filename || docs?.[idx]?.fileName,
            bytes: u.bytes,
            uploadedAt: new Date().toISOString(),
          }));

          const allUploadedDocsForSubmit = [...alreadyUploadedDocs, ...newlyUploadedDocsForState];

          const selectedTransactionId = values?.eventName || null;
          const selectedTx = selectedTransactionId
            ? txItems.find(i => i?.transactionId === selectedTransactionId)
            : null;

          const payload = {
            selectedTransactionId,
            selectedTransaction: selectedTx,
            additionalExpenses: expenses,
            eventUpdate: {
              eventStatus: values?.eventStatus,
              actualEndTime: values?.actualEndTime,
            },
            overtimeDetails: {
              overtimeHours: values?.overtimeHours,
              overtimeRate: values?.overtimeRate,
              totalOvertimeCost: values?.totalOvertimeCost,
              overtimeReason: values?.overtimeReason,
            },
            documents: allUploadedDocsForSubmit.map(d => ({
              url: d.url,
              publicId: d.publicId,
              fileName: d.fileName,
            })),
          };

          // Persist latest uploaded docs into draft before submit completes.
          // This makes sure that if submit fails server-side, reopening modal can rehydrate docs.
          if (selectedTransactionId && newlyUploadedDocsForState.length > 0) {
            setUploadedDocuments(allUploadedDocsForSubmit);
            const draftPayload = buildDraftPayload(values, selectedTransactionId);
            const nextDraftPayload = {
              ...draftPayload,
              documents: {
                uploaded: allUploadedDocsForSubmit,
              },
            };
            await onSaveOvertimeBillDraft(selectedTransactionId, nextDraftPayload, getDraftStorageKey()).catch(() => { });
          }

          const result = onSubmit ? await onSubmit(payload) : undefined;

          // Delete draft after successful submit
          if (selectedTransactionId) {
            onDeleteOvertimeBillDraft(selectedTransactionId, getDraftStorageKey()).catch(() => { });
          }

          if (newlyUploadedDocsForState.length > 0) {
            setUploadedDocuments(allUploadedDocsForSubmit);
          }

          setSelectedDocuments(prev => {
            const arr = Array.isArray(prev) ? prev : [];
            arr.forEach(d => {
              if (d && d.previewUrl) {
                try {
                  URL.revokeObjectURL(d.previewUrl);
                } catch (e) {
                  // no-op
                }
              }
            });
            return [];
          });

          return result;
        } catch (e) {
          setUploadError(e?.message || 'Upload or submission failed.');
          throw e;
        } finally {
          setUploadInProgress(false);
        }
      }}
      render={({ handleSubmit, form, values }) => {
        const isTransactionSelected = Boolean(values?.eventName);
        const disableAllOvertimeFields = !isTransactionSelected || Boolean(isLoading);

        const finalBid = selectedTransactionProtectedData?.finalBid || {};
        const hasFinalBidOvertimeRate =
          finalBid?.overtimeRate !== null &&
          finalBid?.overtimeRate !== undefined &&
          finalBid?.overtimeRate !== '' &&
          Number.isFinite(Number(finalBid?.overtimeRate));
        const isFinalBidOvertimeEnabled = Boolean(finalBid?.overtimeEnabled);
        const shouldLockOvertimeRate = isFinalBidOvertimeEnabled && hasFinalBidOvertimeRate;

        const selectedTransactionId = values?.eventName || null;
        const selectedTransaction = selectedTransactionId
          ? txItems.find(i => i?.transactionId === selectedTransactionId)
          : null;
        const selectedTxProtectedData = selectedTransactionProtectedData;

        const currency = config?.currencyConfig?.currency || 'USD';
        const overtimeHoursDisabled = disableAllOvertimeFields;
        const overtimeRateDisabled = disableAllOvertimeFields || shouldLockOvertimeRate;
        const totalOvertimeCostDisabled = true;

        return (
          <form onSubmit={handleSubmit} className={css.form}>
            <FormSpy
              subscription={{ values: true }}
              render={({ values: spyValues }) => {
                const selectedTransactionId = spyValues?.eventName || null;
                if (selectedTransactionId && lastLoggedTransactionIdRef.current !== selectedTransactionId) {
                  lastLoggedTransactionIdRef.current = selectedTransactionId;
                  const selectedTx = txItems.find(i => i?.transactionId === selectedTransactionId) || null;
                  setSelectedTransactionProtectedData(selectedTx?.protectedData || {});
                  console.log('Selected event/transaction:', selectedTx);
                  console.log('Selected event/transaction protectedData:', selectedTx?.protectedData || {});
                }

                if (!selectedTransactionId && lastLoggedTransactionIdRef.current) {
                  lastLoggedTransactionIdRef.current = null;
                  setSelectedTransactionProtectedData({});
                  console.log('Selected event/transaction: null');
                }

                // Auto-select a draft if none selected
                if (!selectedTransactionId && txItems.length) {
                  const privateData = currentUser?.attributes?.profile?.privateData;
                  const drafts = privateData?.[getDraftStorageKey()];
                  const ids = drafts ? Object.keys(drafts) : [];
                  const matchingId = ids.find(id => txItems.some(t => t?.transactionId === id));
                  if (matchingId && form && typeof form.change === 'function') {
                    form.change('eventName', matchingId);
                  }
                }

                // Hydrate draft when selected tx changes
                if (selectedTransactionId && lastHydratedDraftTxIdRef.current !== selectedTransactionId) {
                  lastHydratedDraftTxIdRef.current = selectedTransactionId;
                  const existingDraft = getExistingDraftForTx(selectedTransactionId);
                  if (existingDraft) {
                    hydrateFromDraft(existingDraft, form);
                  } else {
                    setDraftLoaded(false);
                  }
                }

                // When overtime is enabled in finalBid, lock rate to finalBid.overtimeRate (per 30min)
                // and always calculate totalOvertimeCost.
                const spyFinalBid = selectedTransactionProtectedData?.finalBid || {};
                const spyHasFinalBidRate = Number.isFinite(Number(spyFinalBid?.overtimeRate));
                const spyLockRate = Boolean(spyFinalBid?.overtimeEnabled) && spyHasFinalBidRate;

                if (spyLockRate) {
                  const desiredRate = new Money(Math.round(Number(spyFinalBid.overtimeRate) * 100), currency);
                  const desiredRateKey = `${desiredRate.amount}:${desiredRate.currency}`;
                  if (lastAppliedOvertimeRateRef.current !== desiredRateKey) {
                    lastAppliedOvertimeRateRef.current = desiredRateKey;
                    if (form && typeof form.change === 'function') {
                      form.change('overtimeRate', desiredRate);
                    }
                  }
                } else {
                  lastAppliedOvertimeRateRef.current = null;
                }

                const minutes = parseOvertimeHoursToMinutes(spyValues?.overtimeHours);
                const rateMoney = spyLockRate
                  ? new Money(Math.round(Number(spyFinalBid.overtimeRate) * 100), currency)
                  : spyValues?.overtimeRate instanceof Money
                    ? spyValues.overtimeRate
                    : null;

                if (minutes !== null && rateMoney) {
                  const multiplier = spyLockRate ? minutes / 30 : minutes / 60;
                  const totalSubunits = Math.round(multiplier * rateMoney.amount);
                  const desiredTotal = new Money(totalSubunits, rateMoney.currency || currency);
                  const desiredTotalKey = `${desiredTotal.amount}:${desiredTotal.currency}`;
                  if (lastAppliedTotalRef.current !== desiredTotalKey) {
                    lastAppliedTotalRef.current = desiredTotalKey;
                    if (form && typeof form.change === 'function') {
                      form.change('totalOvertimeCost', desiredTotal);
                    }
                  }
                } else {
                  lastAppliedTotalRef.current = null;
                  if (form && typeof form.change === 'function') {
                    form.change('totalOvertimeCost', null);
                  }
                }

                return null;
              }}
            />

            <div className={css.modalHeader}>
              <h2 className={css.modalTitle}>
                {/* <FormattedMessage id="BillReductionProviderModal.title" defaultMessage="Invoice" /> */}
                Overtime Bill {draftLoaded ? '(Draft)' : ''}
              </h2>
              <button type="button" className={css.closeButton} onClick={onClose}>
                <IconCollection icon="closeModalIcon" />
              </button>
            </div>

            <div className={css.modalContent}>
              <div className={css.section}>
                <div className={css.sectionHeader}>
                  <span className={css.sectionIcon}>
                    <IconCollection icon="calendarDetailIcon" />
                  </span>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage id="BillReductionProviderModal.eventDetails" defaultMessage="Event Details" />
                  </h3>
                </div>
                <div className={css.fieldGroup}>
                  <FieldSelect
                    id="eventName"
                    name="eventName"
                    label={intl.formatMessage({
                      id: 'BillReductionProviderModal.eventNameLabel',
                      defaultMessage: 'Official Event Name',
                    })}
                    className={css.selectField}
                    disabled={Boolean(isLoading)}
                  >
                    <option value="">
                      {intl.formatMessage({
                        id: 'BillReductionProviderModal.selectEventName',
                        defaultMessage: 'Select event name',
                      })}
                    </option>
                    {isLoading ? (
                      <option value="" disabled>
                        Loading events…
                      </option>
                    ) : null}
                    {!isLoading && txItems.length
                      ? txItems.map(item => {
                        const txId = item?.transactionId;
                        const listingTitle = item?.listing?.title || item?.listing?.publicData?.eventTitle;
                        const label = listingTitle || txId;

                        const eventDates = item?.listing?.publicData?.eventDates;
                        const latestEventDate = getLatestEventDate(eventDates);
                        const isInFuture = false;

                        return (
                          <option key={txId} value={txId} disabled={isInFuture}>
                            {label}
                          </option>
                        );
                      })
                      : null}
                  </FieldSelect>

                  {isLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <IconSpinner />
                      <span>
                        {intl.formatMessage({
                          id: 'BillReductionProviderModal.loading',
                          defaultMessage: 'Loading…',
                        })}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={css.section}>
                <div className={css.sectionHeader}>
                  <span className={css.sectionIcon}>
                    <IconCollection icon="documentIcon" />
                  </span>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage
                      id="BillReductionProviderModal.additionalExpenses"
                      defaultMessage="Additional Expenses"
                    />
                  </h3>
                </div>
                <div className={css.expenseRow}>
                  <div className={css.expenseField}>
                    <FieldTextInput
                      id="expenseDraft-category"
                      name="expenseDraft.category"
                      type="text"
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.serviceCategoryLabel',
                        defaultMessage: 'Service Category',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'BillReductionProviderModal.serviceCategoryPlaceholder',
                        defaultMessage: 'Enter service category',
                      })}
                      className={css.field}
                    />
                  </div>
                  <div className={css.quantityField}>
                    <FieldTextInput
                      id="expenseDraft-quantity"
                      name="expenseDraft.quantity"
                      type="number"
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.quantityLabel',
                        defaultMessage: 'Quantity',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'BillReductionProviderModal.quantityPlaceholder',
                        defaultMessage: 'Enter quantity',
                      })}
                      className={css.field}
                    />
                  </div>
                  <div className={css.costField}>
                    <FieldCurrencyInput
                      id="expenseDraft-cost"
                      name="expenseDraft.cost"
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.totalCostLabel',
                        defaultMessage: 'Total Cost',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'BillReductionProviderModal.costPlaceholder',
                        defaultMessage: 'Enter cost',
                      })}
                      currencyConfig={config.currencyConfig}
                      className={css.currencyField}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className={css.addDeductionButton}
                  onClick={() => handleAddExpense(values, form)}
                  disabled={(() => {
                    if (expenses.length >= 3) return true;
                    const draft = values?.expenseDraft || {};
                    const category = (draft?.category || '').trim();
                    const quantity = draft?.quantity;
                    const cost = draft?.cost;
                    const hasQty = quantity !== undefined && quantity !== null && quantity !== '';
                    const hasCost = cost !== undefined && cost !== null && cost !== '';
                    return !(category && hasQty && hasCost);
                  })()}
                >
                  <span className={css.addIcon}>+</span>
                  <FormattedMessage id="BillReductionProviderModal.addExpense" defaultMessage="Add Expense" />
                </button>

                {Array.isArray(expenses) && expenses.length ? (
                  <div className={css.uploadedFiles}>
                    {expenses.map(exp => {
                      const category = exp?.category;
                      const quantity = exp?.quantity;
                      const cost = exp?.cost;
                      const moneyObj = new Money(cost?.amount, cost?.currency);
                      const formatCost = formatMoney(intl, moneyObj);

                      return (
                        <div key={exp.id} className={css.uploadedFile}>
                          <div className={css.fileInfo}>
                            <div className={css.fileDetails}>
                              <span className={css.fileName}>{category}</span>
                              <span className={css.fileSize}>{`Qty: ${quantity} • Cost: ${formatCost}`}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className={css.removeFileButton}
                            onClick={() => handleRemoveExpense(exp.id)}
                            aria-label="Remove expense"
                          >
                            <IconCollection icon="trashDeleteIcon" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className={css.section}>
                <div className={css.sectionHeader}>
                  <span className={css.sectionIcon}>
                    <IconCollection icon="checkCircleIcon" />
                  </span>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage id="BillReductionProviderModal.eventUpdate" defaultMessage="Event Update" />
                  </h3>
                </div>
                <div className={css.eventUpdateRow}>
                  <div className={css.eventUpdateField}>
                    <FieldSelect
                      id="eventStatus"
                      name="eventStatus"
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.eventStatusLabel',
                        defaultMessage: 'Event Status',
                      })}
                      className={css.selectField}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: 'BillReductionProviderModal.selectTime',
                          defaultMessage: 'Select time',
                        })}
                      </option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="in_progress">In Progress</option>
                    </FieldSelect>
                  </div>
                  <div className={css.eventUpdateField}>
                    <FieldTextInput
                      id="actualEndTime"
                      name="actualEndTime"
                      type="time"
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.actualEndTimeLabel',
                        defaultMessage: 'Actual End Time',
                      })}
                      className={css.field}
                    />
                  </div>
                </div>
              </div>



              <div className={css.section}>
                <div className={css.importantNotice}>
                  <div className={css.noticeIcon}>
                    <IconCollection icon="warningCircleIcon" />
                  </div>
                  <div className={css.noticeContent}>
                    <h4 className={css.noticeTitle}>
                      <FormattedMessage
                        id="BillReductionProviderModal.importantNotice"
                        defaultMessage="Important Notice"
                      />
                    </h4>
                    <p className={css.noticeText}>
                      <FormattedMessage
                        id="BillReductionProviderModal.noticeMessage"
                        defaultMessage="Once you mark an event as completed, you cannot change the status back. Please ensure all event activities are finished and all documentation is complete before updating the status."
                      />
                    </p>
                  </div>
                </div>
              </div>




              <div className={css.section}>
                <div className={css.sectionHeader}>
                  <span className={css.sectionIcon}>
                    <IconCollection icon="documentIcon" />
                  </span>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage
                      id="BillReductionProviderModal.overtimeDetails"
                      defaultMessage="Overtime Details"
                    />
                  </h3>
                </div>
                <div className={css.overtimeRow}>
                  <div className={css.overtimeField}>
                    <FieldTextInput
                      id="overtimeHours"
                      name="overtimeHours"
                      type="text"
                      disabled={overtimeHoursDisabled}
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.overtimeHoursLabel',
                        defaultMessage: 'Overtime Hours',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'BillReductionProviderModal.overtimeHoursPlaceholder',
                        defaultMessage: 'Enter time',
                      })}
                      className={css.field}
                    />
                  </div>
                  <div className={css.overtimeField}>
                    <FieldCurrencyInput
                      id="overtimeRate"
                      name="overtimeRate"
                      disabled={overtimeRateDisabled}
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.overtimeRateLabel',
                        defaultMessage: 'Overtime Rate',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'BillReductionProviderModal.overtimeRatePlaceholder',
                        defaultMessage: 'Enter rate',
                      })}
                      currencyConfig={{
                        style: 'currency',
                        currency: 'USD',
                        currencyDisplay: 'symbol',
                        useGrouping: true,
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                      className={css.currencyField}
                    />
                  </div>
                  <div className={css.overtimeField}>
                    <FieldCurrencyInput
                      id="totalOvertimeCost"
                      name="totalOvertimeCost"
                      disabled={totalOvertimeCostDisabled}
                      label={intl.formatMessage({
                        id: 'BillReductionProviderModal.totalOvertimeCostLabel',
                        defaultMessage: 'Total Overtime Cost',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'BillReductionProviderModal.totalOvertimeCostPlaceholder',
                        defaultMessage: 'Enter venue',
                      })}
                      currencyConfig={{
                        style: 'currency',
                        currency: 'USD',
                        currencyDisplay: 'symbol',
                        useGrouping: true,
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                      className={css.currencyField}
                    />
                  </div>
                </div>
                <div className={css.fieldGroup}>
                  <FieldTextInput
                    id="overtimeReason"
                    name="overtimeReason"
                    type="text"
                    disabled={disableAllOvertimeFields}
                    label={intl.formatMessage({
                      id: 'BillReductionProviderModal.overtimeReasonLabel',
                      defaultMessage: 'Reason for Overtime',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'BillReductionProviderModal.overtimeReasonPlaceholder',
                      defaultMessage: 'Enter reason',
                    })}
                    className={css.field}
                  />
                </div>
              </div>





              <div className={css.section}>
                <div className={css.sectionHeader}>
                  <span className={css.sectionIcon}>
                    <IconCollection icon="documentIcon" />
                  </span>
                  <h3 className={css.sectionTitle}>
                    <FormattedMessage
                      id="BillReductionProviderModal.supportingDocument"
                      defaultMessage="Supporting Document"
                    />
                  </h3>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleDocumentSelected}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <div className={css.uploadContainer} onClick={handlePickDocument}>
                  <div>
                    <IconCollection icon="documentUploadIcon" />
                  </div>
                  <span className={css.uploadHeading}>
                    <FormattedMessage
                      id="BillReductionProviderModal.uploadDocuments"
                      defaultMessage="Upload Supporting Documents"
                    />
                  </span>
                  <div className={css.uploadSubHeading}>
                    <FormattedMessage
                      id="BillReductionProviderModal.uploadDescription"
                      defaultMessage="Photos, receipts, client communications, or any other relevant files"
                    />
                  </div>
                </div>
                {uploadError ? <div className={css.errorText}>{uploadError}</div> : null}
                {(() => {
                  const uploaded = Array.isArray(uploadedDocuments) ? uploadedDocuments : [];
                  const selected = Array.isArray(selectedDocuments) ? selectedDocuments : [];
                  const docsToShow = [...uploaded, ...selected];

                  if (docsToShow.length === 0) {
                    return null;
                  }

                  return (
                    <div className={css.uploadedFiles}>
                      {docsToShow.map(doc => {
                        const isUploaded = !!doc?.url;
                        const docKey = doc?.id || doc?.publicId || doc?.fileName || doc?.original_filename;
                        const name = doc?.fileName || doc?.original_filename || 'File';
                        const sizeBytes = typeof doc?.bytes === 'number' ? doc.bytes : null;
                        const sizeMb = sizeBytes ? (sizeBytes / (1024 * 1024)).toFixed(2) : null;
                        const isPdf = String(name).toLowerCase().endsWith('.pdf');
                        const icon = isPdf ? 'filePdfIcon' : 'fileImageIcon';

                        return (
                          <div key={docKey || name} className={css.uploadedFile}>
                            <div className={css.fileInfo}>
                              <div className={css.fileIcon}>
                                {!isUploaded && doc?.previewUrl ? (
                                  <img
                                    src={doc.previewUrl}
                                    alt={name}
                                    style={{ width: 32, height: 32, objectFit: 'cover' }}
                                  />
                                ) : (
                                  <IconCollection icon={icon} />
                                )}
                              </div>
                              <div className={css.fileDetails}>
                                <span className={css.fileName}>{name}</span>
                                <span className={css.fileSize}>{sizeMb ? `${sizeMb} MB` : ''}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className={css.removeFileButton}
                              onClick={() =>
                                isUploaded
                                  ? handleRemoveUploadedDocument(doc?.id || doc?.publicId, values)
                                  : handleRemoveDocument(doc.id)
                              }
                              aria-label="Remove file"
                            >
                              <IconCollection icon="trashDeleteIcon" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className={css.modalFooter}>
              <Button
                className={css.saveDraftButton}
                inProgress={saveDraftInProgress}
                disabled={
                  saveDraftInProgress ||
                  uploadInProgress ||
                  !hasAnyDraftInput(values)
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (saveDraftInProgress) {
                    return;
                  }

                  const txId = values?.eventName;
                  if (!txId) {
                    return;
                  }

                  setUploadError(null);
                  setSaveDraftInProgress(true);

                  Promise.resolve()
                    .then(() => uploadNewSelectedDocuments())
                    .then(newUploaded => {
                      const existingUploaded = Array.isArray(uploadedDocuments) ? uploadedDocuments : [];
                      const uploaded = [...existingUploaded, ...(Array.isArray(newUploaded) ? newUploaded : [])];

                      const draftPayload = buildDraftPayload(values, txId);
                      const nextDraftPayload = {
                        ...draftPayload,
                        documents: {
                          uploaded,
                        },
                      };

                      return onSaveOvertimeBillDraft(txId, nextDraftPayload, getDraftStorageKey());
                    })
                    .then(() => {
                      onClose();
                    })
                    .catch(err => {
                      setUploadError(err?.message || 'Failed to save draft.');
                    })
                    .finally(() => {
                      setSaveDraftInProgress(false);
                    });
                }}>
                Save Draft
              </Button>


              <Button
                type="submit"
                className={css.submitButton}
                inProgress={submitting}
                disabled={isSubmitDisabled(values)}
              >
                <FormattedMessage id="CreateOvertimeBillProviderModal.submitRequest" defaultMessage="Create Invoice" />
              </Button>

            </div>
          </form>
        );

      }}
    />
  );
};

const mapStateToProps = state => {
  const currentUser = state.user.currentUser;
  return { currentUser };
};

const mapDispatchToProps = dispatch => ({
  onSaveOvertimeBillDraft: (transactionId, draft, storageKey) =>
    dispatch(saveOvertimeBillDraft(transactionId, draft, storageKey)),
  onDeleteOvertimeBillDraft: (transactionId, storageKey) =>
    dispatch(deleteOvertimeBillDraft(transactionId, storageKey)),
});

const CreateInvoiceOvertimeProviderModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateInvoiceOvertimeProviderModalComponent);

export default CreateInvoiceOvertimeProviderModal;
